import { type BoatFilters } from '@/components/boat-filter-sidebar';
import { type FilterOptions } from '@/types';
import { type AlertCriteriaLabel, type SearchAlertCriteria, type SearchAlertFormErrors, type SearchAlertItem, type SearchAlertSource } from '@/types/search-alert';
import { type SearchCriteriaData } from '@/types/search-criteria';

/** Query parameter appended to the return URL when a guest clicks "Criar alerta" (the modal reopens after login). */
export const CREATE_ALERT_QUERY_PARAM = 'criar_alerta';

const CREATE_ALERT_DRAFT_KEY = 'search_alert_create_draft';
const MAX_ALERT_NAME_LENGTH = 120;
const DEFAULT_ALERT_NAME = 'Alerta de embarcações';

const ALERT_SOURCES: SearchAlertSource[] = ['busca', 'busca_ia', 'busca_sem_resultado', 'parecidas_anuncio', 'anuncio_inativo'];

interface BoatFilterBounds {
    yearMin: number;
    yearMax: number;
    priceMin: number;
    priceMax: number;
    hoursMin: number;
    hoursMax: number;
}

interface BuildCriteriaOptions {
    filterOptions: FilterOptions;
    bounds: BoatFilterBounds;
    /** Name of a category that is not in filterOptions (e.g. the category of a /lanchas page). */
    resolveCategoryName?: (categoryId: number) => string | undefined;
}

/**
 * Draft kept in sessionStorage while the guest logs in. Only form choices, never phone or consent.
 */
export interface CreateAlertDraft {
    name?: string;
    validity_days?: number;
    channel_email?: boolean;
    channel_whatsapp?: boolean;
    source: SearchAlertSource;
    created_at: string;
}

const compactNumber = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const plainNumber = new Intl.NumberFormat('pt-BR');

/** "R$ 300 mil", "R$ 1,2 mi", "R$ 850". */
export const formatCompactPrice = (value: number): string => {
    if (value >= 1_000_000) {
        return `R$ ${compactNumber.format(value / 1_000_000)} mi`;
    }

    if (value >= 1_000) {
        return `R$ ${compactNumber.format(value / 1_000)} mil`;
    }

    return `R$ ${plainNumber.format(value)}`;
};

export const formatYearChip = (min?: number | null, max?: number | null): string | null => {
    if (min && max) {
        return `${min} a ${max}`;
    }

    if (min) {
        return `${min} em diante`;
    }

    if (max) {
        return `até ${max}`;
    }

    return null;
};

export const formatPriceChip = (min?: number | null, max?: number | null): string | null => {
    if (min && max) {
        return `${formatCompactPrice(min)} a ${formatCompactPrice(max)}`;
    }

    // Same capitalization as the search/modal labels (SearchCriteriaBuilder::rangeLabel): "Até R$ 300 mil".
    if (max) {
        return `Até ${formatCompactPrice(max)}`;
    }

    if (min) {
        return `A partir de ${formatCompactPrice(min)}`;
    }

    return null;
};

export const formatHoursChip = (min?: number | null, max?: number | null): string | null => {
    if (min && max) {
        return `${plainNumber.format(min)} a ${plainNumber.format(max)} h de uso`;
    }

    if (max) {
        return `até ${plainNumber.format(max)} h de uso`;
    }

    if (min) {
        return `a partir de ${plainNumber.format(min)} h de uso`;
    }

    return null;
};

export const formatSizeChip = (min?: number | null, max?: number | null): string | null => {
    if (min && max) {
        return `${plainNumber.format(min)} a ${plainNumber.format(max)} pés`;
    }

    if (max) {
        return `até ${plainNumber.format(max)} pés`;
    }

    if (min) {
        return `acima de ${plainNumber.format(min)} pés`;
    }

    return null;
};

/** Suggested name of an alert from its chips (first three). */
export const buildDefaultAlertName = (chips: string[]): string => {
    const name = chips.slice(0, 3).join(' · ').trim();

    if (!name) {
        return DEFAULT_ALERT_NAME;
    }

    return name.length > MAX_ALERT_NAME_LENGTH ? `${name.slice(0, MAX_ALERT_NAME_LENGTH - 1).trimEnd()}…` : name;
};

/**
 * Criteria of the alert from the package the server built for the current search (CN-200 item 10.2): the structured
 * parameters (free text out), the labels with their removal keys and the suggested name (the phrase when there is one).
 * The free text of the search (label "search") never enters the alert; it is returned apart so the modal can say so.
 */
export const buildAlertCriteriaFromSearch = (criteria: SearchCriteriaData, { useOriginalText = true }: { useOriginalText?: boolean } = {}): SearchAlertCriteria => {
    const searchLabel = criteria.labels.find((label) => label.id === 'search');
    const labels: AlertCriteriaLabel[] = criteria.labels
        .filter((label) => label.id !== 'search')
        .map((label) => ({
            id: label.id,
            text: label.text,
            remove_keys: label.remove_keys,
            value: label.value,
            replace_with: label.replace_with ?? null,
        }));
    const originalText = useOriginalText ? (criteria.original_text ?? null) : null;
    const excludedText = searchLabel?.value?.trim() || null;

    return {
        params: { ...criteria.alert_params },
        labels,
        defaultName: originalText ? originalText.slice(0, MAX_ALERT_NAME_LENGTH) : buildDefaultAlertName(labels.map((label) => label.text)),
        originalText,
        excludedText,
    };
};

/**
 * Parameters left after removing some chips: each chip drops its keys (or only its value in a list) and may set the
 * parent value (categoria > fabricante > modelo).
 */
export const applyLabelRemovals = (criteria: SearchAlertCriteria, removedIds: Set<string>): { params: Record<string, unknown>; labels: AlertCriteriaLabel[] } => {
    const params: Record<string, unknown> = { ...criteria.params };

    for (const label of criteria.labels) {
        if (!removedIds.has(label.id)) {
            continue;
        }

        for (const key of label.remove_keys) {
            const current = params[key];

            if (label.value !== null && Array.isArray(current)) {
                const remaining = current.filter((item) => String(item) !== label.value);

                if (remaining.length === 0) {
                    delete params[key];
                } else {
                    params[key] = remaining;
                }

                continue;
            }

            delete params[key];
        }

        if (label.replace_with) {
            Object.assign(params, label.replace_with);
        }
    }

    return { params, labels: criteria.labels.filter((label) => !removedIds.has(label.id)) };
};

/**
 * Converts the sidebar filters into the parameters of the alert and the chips shown in the modal (fallback used when
 * the page has no server-built criteria package). Range values equal to the slider bounds mean "no filter".
 */
export const buildSearchAlertCriteria = (
    filters: BoatFilters,
    { filterOptions, bounds, resolveCategoryName }: BuildCriteriaOptions,
): SearchAlertCriteria => {
    const params: Record<string, unknown> = {};
    const labels: AlertCriteriaLabel[] = [];
    const add = (id: string, text: string, remove_keys: string[], value: string | null = null) => labels.push({ id, text, remove_keys, value });

    const categoryNames = new Map<number, string>(filterOptions.categories.map((category) => [category.id, category.name]));
    const categoryIds = filters.category.map(Number).filter((id) => Number.isFinite(id) && id > 0);

    if (categoryIds.length > 0) {
        params.category = categoryIds.map(String);

        for (const id of categoryIds) {
            add(`category:${id}`, categoryNames.get(id) ?? resolveCategoryName?.(id) ?? `Categoria ${id}`, ['category'], String(id));
        }
    }

    if (filters.location) {
        if (filters.location.type === 'state') {
            params.state = filters.location.slug || filters.location.id;
            add('location', filters.location.label, ['state']);
        } else {
            params.city = filters.location.id;

            if (filters.radius !== null) {
                params.radius = filters.radius;
            }

            add('location', filters.radius ? `${filters.location.label} · ${filters.radius} km` : filters.location.label, ['city', 'radius']);
        }
    }

    if (filters.condition !== 'all') {
        params.condition = filters.condition;
        add('condition', filters.condition === 'new' ? 'Novos' : 'Usados', ['condition']);
    }

    const yearMin = filters.yearRange[0] !== bounds.yearMin ? filters.yearRange[0] : undefined;
    const yearMax = filters.yearRange[1] !== bounds.yearMax ? filters.yearRange[1] : undefined;
    const priceMin = filters.priceRange[0] !== bounds.priceMin ? filters.priceRange[0] : undefined;
    const priceMax = filters.priceRange[1] !== bounds.priceMax ? filters.priceRange[1] : undefined;
    const hoursMin = filters.hours[0] > bounds.hoursMin ? filters.hours[0] : undefined;
    const hoursMax = filters.hours[1] < bounds.hoursMax ? filters.hours[1] : undefined;

    if (yearMin !== undefined) params.year_min = yearMin;
    if (yearMax !== undefined) params.year_max = yearMax;
    if (priceMin !== undefined) params.price_min = priceMin;
    if (priceMax !== undefined) params.price_max = priceMax;
    if (hoursMin !== undefined) params.sailing_time_min = hoursMin;
    if (hoursMax !== undefined) params.sailing_time_max = hoursMax;
    if (filters.sizeMin !== null) params.size_min = filters.sizeMin;
    if (filters.sizeMax !== null) params.size_max = filters.sizeMax;

    const yearChip = formatYearChip(yearMin, yearMax);
    const priceChip = formatPriceChip(priceMin, priceMax);
    const sizeChip = formatSizeChip(filters.sizeMin, filters.sizeMax);
    const hoursChip = formatHoursChip(hoursMin, hoursMax);

    if (yearChip) add('year', yearChip, ['year_min', 'year_max']);
    if (priceChip) add('price', priceChip, ['price_min', 'price_max']);
    if (sizeChip) add('size', sizeChip, ['size_min', 'size_max']);
    if (hoursChip) add('hours', hoursChip, ['sailing_time_min', 'sailing_time_max']);

    if (filters.fuelType) {
        params.fuel_type = filters.fuelType;
        add('fuel_type', `Combustível: ${filters.fuelType}`, ['fuel_type']);
    }
    if (filters.engineBrand) {
        params.engine_brand = filters.engineBrand;
        add('engine_brand', `Motor ${filters.engineBrand}`, ['engine_brand']);
    }
    if (filters.cabin) {
        params.cabin = 1;
        add('cabin', 'Com cabine', ['cabin']);
    }
    if (filters.bathroom) {
        params.bathroom = 1;
        add('bathroom', 'Com banheiro', ['bathroom']);
    }
    if (filters.generator) {
        params.generator = 1;
        add('generator', 'Com gerador', ['generator']);
    }
    if (filters.passengersMin) {
        params.passengers_min = filters.passengersMin;
        add('passengers_min', `${filters.passengersMin}+ passageiros`, ['passengers_min']);
    }

    return { params, labels, defaultName: buildDefaultAlertName(labels.map((label) => label.text)), originalText: null };
};

/**
 * Criteria of the alert offered on the page of an inactive classified: the catalog hierarchy of the boat (categoria >
 * fabricante > modelo) plus its state (UF), all removable (atualização 17/10, item C). The search filters categories
 * with OR, so the parameter carries only the most specific category; removing a level falls back to its parent.
 *
 * @param hierarchy catalog categories of the boat from the root to the leaf (nulls are skipped)
 */
export const buildClassifiedCategoryAlertCriteria = (
    hierarchy: Array<{ id: number; name: string } | null | undefined>,
    state?: { id: number; slug?: string | null; label: string } | null,
): SearchAlertCriteria => {
    const levels = hierarchy.filter((category): category is { id: number; name: string } => Boolean(category && category.id > 0));
    const leaf = levels[levels.length - 1];
    const labels: AlertCriteriaLabel[] = levels.map((category, index) => ({
        id: `category:${category.id}`,
        text: category.name,
        remove_keys: ['category'],
        value: null,
        replace_with: index > 0 ? { category: [String(levels[index - 1].id)] } : null,
    }));
    const params: Record<string, unknown> = leaf ? { category: [String(leaf.id)] } : {};

    if (state) {
        params.state = state.slug || state.id;
        labels.push({ id: 'location', text: state.label, remove_keys: ['state'], value: null });
    }

    return { params, labels, defaultName: buildDefaultAlertName(labels.map((label) => label.text)), originalText: null };
};

/** Chips of a saved alert (presenter fields), in the same order as the modal. */
export const buildSearchAlertItemChips = (alert: SearchAlertItem): string[] => {
    const chips: string[] = [];

    if (alert.category) {
        chips.push(
            ...alert.category
                .split(',')
                .map((name) => name.trim())
                .filter(Boolean),
        );
    }

    if (alert.location) {
        chips.push(alert.location);
    }

    if (alert.condition) {
        chips.push(alert.condition);
    }

    const yearChip = formatYearChip(alert.year_min, alert.year_max);
    const priceChip = formatPriceChip(alert.price_min, alert.price_max);
    const sizeChip = formatSizeChip(alert.filters?.size_min, alert.filters?.size_max);
    const hoursChip = formatHoursChip(alert.filters?.sailing_time_min, alert.hours_max);

    if (yearChip) chips.push(yearChip);
    if (priceChip) chips.push(priceChip);
    if (sizeChip) chips.push(sizeChip);
    if (hoursChip) chips.push(hoursChip);

    const filters = alert.filters ?? {};
    if (filters.fuel_type) chips.push(`Combustível: ${filters.fuel_type}`);
    if (filters.engine_brand) chips.push(`Motor ${filters.engine_brand}`);
    if (filters.cabin) chips.push('Com cabine');
    if (filters.bathroom) chips.push('Com banheiro');
    if (filters.generator) chips.push('Com gerador');
    if (filters.passengers_min) chips.push(`${filters.passengers_min}+ passageiros`);

    return chips;
};

/** First message per field from a Laravel 422 payload or an Inertia `errors` bag. */
export const toSearchAlertFormErrors = (errors: unknown): SearchAlertFormErrors => {
    if (!errors || typeof errors !== 'object') {
        return {};
    }

    const result: SearchAlertFormErrors = {};

    for (const [field, value] of Object.entries(errors as Record<string, unknown>)) {
        if (Array.isArray(value)) {
            const first = value.find((item) => typeof item === 'string');
            if (first) result[field] = first;
        } else if (typeof value === 'string') {
            result[field] = value;
        }
    }

    return result;
};

/** Field errors of an axios error with a 422 response; null for any other failure. */
export const extractValidationErrors = (error: unknown): SearchAlertFormErrors | null => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return null;
    }

    const response = (error as { response?: { status?: number; data?: { errors?: unknown } } }).response;

    if (response?.status !== 422) {
        return null;
    }

    return toSearchAlertFormErrors(response.data?.errors);
};

export const saveCreateAlertDraft = (draft: CreateAlertDraft): void => {
    if (typeof window === 'undefined') {
        return;
    }

    window.sessionStorage.setItem(CREATE_ALERT_DRAFT_KEY, JSON.stringify(draft));
};

export const readCreateAlertDraft = (): CreateAlertDraft | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const raw = window.sessionStorage.getItem(CREATE_ALERT_DRAFT_KEY);

    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as Partial<CreateAlertDraft>;

        if (!parsed.source || !ALERT_SOURCES.includes(parsed.source)) {
            return null;
        }

        return {
            name: typeof parsed.name === 'string' ? parsed.name : undefined,
            validity_days: typeof parsed.validity_days === 'number' ? parsed.validity_days : undefined,
            channel_email: typeof parsed.channel_email === 'boolean' ? parsed.channel_email : undefined,
            channel_whatsapp: typeof parsed.channel_whatsapp === 'boolean' ? parsed.channel_whatsapp : undefined,
            source: parsed.source,
            created_at: typeof parsed.created_at === 'string' ? parsed.created_at : new Date().toISOString(),
        };
    } catch {
        // A corrupted draft is just discarded: the user re-opens the modal manually.
        return null;
    }
};

export const clearCreateAlertDraft = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    window.sessionStorage.removeItem(CREATE_ALERT_DRAFT_KEY);
};

/** Return URL of the login flow: the current search with the "reopen the modal" flag. */
export const buildCreateAlertReturnUrl = (): string => {
    const url = new URL(window.location.href);
    url.searchParams.set(CREATE_ALERT_QUERY_PARAM, '1');

    return url.toString();
};

/** Removes the "reopen the modal" flag from the address bar without a navigation. */
export const clearCreateAlertQueryParam = (): void => {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(CREATE_ALERT_QUERY_PARAM)) {
        return;
    }

    url.searchParams.delete(CREATE_ALERT_QUERY_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
};

export const formatAlertDate = (value: string | null | undefined): string | null => {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toLocaleDateString('pt-BR');
};
